import * as React from 'react';

import { Box, H1, Link, Text } from '@opendoor/bricks/core';

import { Illustration } from '../Illustration';

type PasswordlessResultPageProps = {
  /**
   * Boolean representing whether the passwordless login request succeeded
   */
  hasSucceeded: boolean;
  /**
   * Email of the user requesting a passwordless login
   */
  email: string;
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
};

const PasswordlessResultPage = ({
  email,
  hasSucceeded,
  analyticsName,
}: PasswordlessResultPageProps) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" mb={12}>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <Illustration
        type="mixMatch"
        illustrationName="People/UI-2"
        width={['197px', null, '340px']}
        height={['151px', null, '260px']}
        mb={[7, null, 10]}
        objectFit="cover"
      />
      <H1 fontSize={['s4', null, 's6']} lineHeight={['s4', null, 's6']} mb={[5, null, 8]}>
        {hasSucceeded ? 'Check your email!' : 'Something went wrong.'}
      </H1>
      <Text
        fontSize={['s1', null, 's3']}
        lineHeight={['s1', null, 's3']}
        color="neutrals90"
        textAlign="center"
        maxWidth="675px"
      >
        {hasSucceeded ? (
          <>
            We sent an email to{' '}
            <Text as="span" fontWeight="semibold">
              {email}
            </Text>
            .
            <br />
            It includes a link that will sign you in.
          </>
        ) : (
          <>
            We are unable to send you an email. Please reach out to{' '}
            <Link
              variant="inline-secondary"
              href="mailto:contact@opendoor.com"
              aria-label={null}
              analyticsName={`${analyticsName}-passwordless-error-contact-us`}
            >
              contact@opendoor.com
            </Link>{' '}
            for support.
          </>
        )}
      </Text>
    </Box>
  );
};

export default PasswordlessResultPage;
